import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApiQuery } from 'grabbitbot-legacy-ui'
import { connections, telegram } from '../../api/fee'
import type { ProjectConnectionSettings } from '../../api/types'
import { Notice, PageTitle, QueryState } from '../../components/common'
import { TELEGRAM_CONNECT_BOT } from '../../config'
import { formatDateTime } from '../../lib/format'
import { useMoneyAction } from '../../lib/useMoneyAction'

/** The official Telegram Login Widget (legacy injected the same script);
 * after login Telegram redirects to `/connections/telegram/check`. */
function TelegramLoginWidget() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const host = ref.current
    if (!host || host.childElementCount > 0) return
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.setAttribute('data-telegram-login', TELEGRAM_CONNECT_BOT)
    script.setAttribute('data-size', 'small')
    script.setAttribute('data-userpic', 'false')
    script.setAttribute('data-auth-url', `${window.location.origin}/connections/telegram/check`)
    script.setAttribute('data-request-access', 'write')
    host.appendChild(script)
  }, [])
  return (
    <div>
      <div ref={ref} data-testid="telegram-widget" />
      <p className="portal-hint">* if the button doesn&apos;t appear, please refresh the page</p>
    </div>
  )
}

/**
 * Port of `TelegramConnectionComponent` (`/connections/telegram`): the
 * caller's own Telegram connection (`GET /connections/telegram`), the login
 * widget to (re)connect, and which projects it is shared with
 * (`GET|PATCH /connectionsettings`, "Save All").
 */
export function TelegramConnectionPage() {
  const conn = useApiQuery(() => telegram.connection(), [])
  const all = useApiQuery(() => connections.allSettings(), [])
  const [items, setItems] = useState<ProjectConnectionSettings[]>([])
  const actions = useMoneyAction()
  useEffect(() => {
    if (all.data) setItems(all.data.map((i) => ({ ...i, connectionSettings: { ...i.connectionSettings } })))
  }, [all.data])

  const saveAll = async () => {
    const r = await actions.run('save', JSON.stringify(items), () => connections.modifyAllSettings(items))
    if (r !== undefined) {
      actions.setFeedback('save', { tone: 'success', text: 'Saved.' })
      all.refetch()
    }
  }

  const c = conn.data
  return (
    <div className="portal-card">
      <PageTitle title="Telegram connection" />
      <div className="portal-grid-2">
        <div>
          <QueryState loading={conn.loading} error={conn.error}>
            {c && c.connected ? (
              <div className="fs-panel fs-panel--success">
                <dl className="portal-kv">
                  <dt>First connection</dt>
                  <dd>{formatDateTime(c.createdAt)}</dd>
                  {c.lastModAt && c.lastModAt !== c.createdAt && (
                    <>
                      <dt>Last info refresh</dt>
                      <dd>{formatDateTime(c.lastModAt)}</dd>
                    </>
                  )}
                  <dt>User ID</dt>
                  <dd>{c.data?.user_id}</dd>
                  <dt>Username</dt>
                  <dd>{c.data?.username}</dd>
                  <dt>Last name</dt>
                  <dd>{c.data?.last_name}</dd>
                  <dt>First name</dt>
                  <dd>{c.data?.first_name}</dd>
                </dl>
              </div>
            ) : (
              <div className="fs-panel fs-panel--danger">Not connected yet</div>
            )}
          </QueryState>
          <TelegramLoginWidget />
        </div>
        <div>
          <h2>This connection is shared with projects:</h2>
          <QueryState loading={all.loading} error={all.error}>
            <ul className="fs-plain-list">
              {items.map((it, i) => (
                <li key={it.project.id}>
                  <label className="fs-switch">
                    <input
                      type="checkbox"
                      role="switch"
                      checked={Boolean(it.connectionSettings.telegramShared)}
                      onChange={(e) => {
                        const next = [...items]
                        next[i] = { ...it, connectionSettings: { ...it.connectionSettings, telegramShared: e.target.checked ? 1 : 0 } }
                        setItems(next)
                      }}
                    />
                    <Link to={`/projects/${encodeURIComponent(it.project.id)}`}>{it.project.name}</Link>
                  </label>
                </li>
              ))}
            </ul>
            {items.length > 0 && (
              <button type="button" disabled={actions.isBusy('save')} onClick={() => void saveAll()}>
                {actions.isBusy('save') ? 'Saving…' : 'Save All'}
              </button>
            )}
            <Notice feedback={actions.feedbackFor('save')} />
          </QueryState>
        </div>
      </div>
    </div>
  )
}
